import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { customerHistoryLabel } from '../governance/customer-history-label'
import {
  loadOrganisationAuditHistory,
  type OrganisationAuditCategory,
} from './audit-history-api'

const administrationHistoryLabels: Record<string, string> = {
  'organisation.created': 'Organisation created',
  'organisation.profile_updated': 'Organisation details updated',
  'organisation.invitation_created': 'Invitation created',
  'organisation.invitation_accepted': 'Invitation accepted',
  'organisation.invitation_resent': 'Invitation resent',
  'organisation.invitation_revoked': 'Invitation cancelled',
  'organisation.membership_roles_changed': 'Organisation access roles changed',
  'organisation.people_access_roles_changed': 'Organisation access roles changed',
  'organisation.membership_status_changed': 'Organisation access status changed',
}

function historyLabel(eventType: string) {
  return administrationHistoryLabels[eventType] ?? customerHistoryLabel(eventType)
}

function periodStart(period: string): string | null {
  if (period === 'all') return null
  const days = period === '30' ? 30 : 90
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString()
}

function categoryLabel(category: OrganisationAuditCategory) {
  return category === 'access' ? 'People & access' : 'Governance'
}

export function AuditHistoryPanel({ organisationId, canReadGovernance }: { organisationId: string; canReadGovernance: boolean }) {
  const [category, setCategory] = useState<'all' | OrganisationAuditCategory>('all')
  const [period, setPeriod] = useState<'30' | '90' | 'all'>('90')
  const [actor, setActor] = useState('all')
  const [visibleCount, setVisibleCount] = useState(25)

  const auditQuery = useQuery({
    queryKey: ['organisation-audit-history', organisationId, category, period],
    queryFn: () => loadOrganisationAuditHistory({
      organisationId,
      category: category === 'all' ? null : category,
      from: periodStart(period),
      limit: 200,
    }),
    enabled: Boolean(organisationId),
  })

  const actors = useMemo(
    () => Array.from(new Set((auditQuery.data ?? []).map((event) => event.actorName))).sort((a, b) => a.localeCompare(b)),
    [auditQuery.data],
  )
  const visibleEvents = useMemo(
    () => (auditQuery.data ?? []).filter((event) => actor === 'all' || event.actorName === actor),
    [actor, auditQuery.data],
  )
  const displayedEvents = visibleEvents.slice(0, visibleCount)
  useEffect(() => setVisibleCount(25), [actor, category, period])
  const hasCachedHistory = auditQuery.data !== undefined
  const readFailureMessage = hasCachedHistory
    ? 'Audit history could not be refreshed. Showing the last loaded history.'
    : 'Audit history could not be loaded. Please try again.'

  return (
    <section className="panel" aria-labelledby="organisation-audit-history-heading">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Organisation</p>
          <h2 id="organisation-audit-history-heading">Audit history</h2>
          <p>{canReadGovernance ? 'Read-only history of governance and access changes.' : 'Read-only history of organisation administration and access changes. Governance activity is available only to Governance Managers.'} Historical audit records cannot be edited or deleted.</p>
        </div>
        <span className="status-chip neutral">Read only</span>
      </div>

      <div className="admin-form-grid" aria-label="Audit history filters">
        <label>
          <span>Activity</span>
          <select value={category} onChange={(event) => { setCategory(event.target.value as typeof category); setActor('all') }}>
            <option value="all">All activity</option>
            {canReadGovernance ? <option value="governance">Governance</option> : null}
            <option value="access">People &amp; access</option>
          </select>
        </label>
        <label>
          <span>Period</span>
          <select value={period} onChange={(event) => { setPeriod(event.target.value as typeof period); setActor('all') }}>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="all">All available history</option>
          </select>
        </label>
        <label>
          <span>Person</span>
          <select value={actor} onChange={(event) => setActor(event.target.value)}>
            <option value="all">All people</option>
            {actors.map((actorName) => <option value={actorName} key={actorName}>{actorName}</option>)}
          </select>
        </label>
      </div>

      {auditQuery.isLoading ? <div className="loading-line">Loading audit history…</div> : null}
      {auditQuery.isError ? (
        <div className="inline-alert danger" role="alert">
          <span>{readFailureMessage}</span>
          <div className="inline-actions">
            <button className="button text compact" type="button" onClick={() => void auditQuery.refetch()} disabled={auditQuery.isFetching}>Retry</button>
          </div>
        </div>
      ) : null}
      {auditQuery.isSuccess && visibleEvents.length === 0 ? (
        <div className="empty-state"><h3>No matching history</h3><p>Try a different activity, period or person filter.</p></div>
      ) : null}

      {visibleEvents.length > 0 ? (
        <div className="member-list" aria-live="polite">
          <h3>Recorded activity</h3>
          {displayedEvents.map((event) => (
            <article className="member-row" key={event.id}>
              <div className="member-identity">
                <div>
                  <strong>{historyLabel(event.eventType)}</strong>
                  <span>{event.actorName}</span>
                  <span>{new Date(event.occurredAt).toLocaleString('en-AU')}</span>
                </div>
              </div>
              <div className="member-role-area">
                <span className="status-chip neutral">{categoryLabel(event.category)}</span>
              </div>
            </article>
          ))}
          {displayedEvents.length < visibleEvents.length ? <button className="button secondary audit-load-more" type="button" onClick={() => setVisibleCount((count) => count + 25)}>Load more</button> : null}
        </div>
      ) : null}
    </section>
  )
}
